// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { BookOpen, ExternalLink, Download, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import './KOReaderIntegration.css';
import { apiFetch } from '../config/api';

const KOReaderIntegration = ({ bookId, filePath, fileName }) => {
    const [koreaderStatus, setKoreaderStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [syncStatus, setSyncStatus] = useState(null);

    // Vérifier le statut de KOReader au chargement
    useEffect(() => {
        checkKOReaderStatus();
    }, []);

    const checkKOReaderStatus = async () => {
        try {
            const response = await apiFetch('koreader/status');
            const data = await response.json();
            setKoreaderStatus(data);
        } catch (err) {
            console.error('Erreur vérification KOReader:', err);
            setError('Impossible de vérifier le statut de KOReader');
        }
    };

    const openWithKOReader = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await apiFetch('koreader/open', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    file_path: filePath,
                    book_id: bookId
                })
            });

            const data = await response.json();

            if (data.success) {
                // Succès - KOReader lancé
                setError(null);
                // Optionnel : afficher une notification de succès
            } else {
                setError(data.error || 'Erreur lors du lancement de KOReader');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        } finally {
            setIsLoading(false);
        }
    };

    const syncWithKOReader = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await apiFetch('koreader/sync-from-calibre', {
                method: 'POST'
            });

            const data = await response.json();

            if (data.success) {
                setSyncStatus({
                    success: true,
                    message: data.message,
                    count: data.synced_count
                });
            } else {
                setError(data.error || 'Erreur de synchronisation');
            }
        } catch (err) {
            setError('Erreur de connexion au serveur');
        } finally {
            setIsLoading(false);
        }
    };

    const checkFormatSupport = async () => {
        try {
            const response = await apiFetch('koreader/check-format', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    file_path: filePath
                })
            });

            const data = await response.json();
            return data.supported;
        } catch (err) {
            console.error('Erreur vérification format:', err);
            return false;
        }
    };

    const renderKOReaderButton = () => {
        if (!koreaderStatus?.available) {
            return (
                <div className="koreader-not-available">
                    <AlertCircle size={20} />
                    <span>KOReader non disponible</span>
                    <button 
                        className="install-koreader-btn"
                        onClick={() => window.open('https://github.com/koreader/koreader', '_blank')}
                    >
                        <Download size={16} />
                        Installer KOReader
                    </button>
                </div>
            );
        }

        const isSupported = koreaderStatus.supported_formats?.includes(
            fileName.split('.').pop()?.toLowerCase()
        );

        if (!isSupported) {
            return (
                <div className="koreader-format-not-supported">
                    <AlertCircle size={20} />
                    <span>Format non supporté par KOReader</span>
                </div>
            );
        }

        return (
            <button 
                className="koreader-open-btn"
                onClick={openWithKOReader}
                disabled={isLoading}
            >
                {isLoading ? (
                    <RefreshCw size={16} className="spinning" />
                ) : (
                    <BookOpen size={16} />
                )}
                Ouvrir avec KOReader
            </button>
        );
    };

    return (
        <div className="koreader-integration">
            <div className="koreader-header">
                <h3>
                    <BookOpen size={20} />
                    Lecteur KOReader
                </h3>
                <button 
                    className="refresh-btn"
                    onClick={checkKOReaderStatus}
                    title="Actualiser le statut"
                >
                    <RefreshCw size={16} />
                </button>
            </div>

            {/* Statut KOReader */}
            {koreaderStatus && (
                <div className="koreader-status">
                    <div className="status-item">
                        <span className="label">Disponibilité:</span>
                        <span className={`value ${koreaderStatus.available ? 'success' : 'error'}`}>
                            {koreaderStatus.available ? (
                                <>
                                    <CheckCircle size={16} />
                                    Disponible
                                </>
                            ) : (
                                <>
                                    <AlertCircle size={16} />
                                    Non disponible
                                </>
                            )}
                        </span>
                    </div>

                    {koreaderStatus.version?.success && (
                        <div className="status-item">
                            <span className="label">Version:</span>
                            <span className="value">{koreaderStatus.version.version}</span>
                        </div>
                    )}

                    <div className="status-item">
                        <span className="label">Formats supportés:</span>
                        <span className="value">{koreaderStatus.supported_formats?.length || 0} formats</span>
                    </div>
                </div>
            )}

            {/* Bouton d'ouverture */}
            <div className="koreader-actions">
                {renderKOReaderButton()}
            </div>

            {/* Synchronisation */}
            <div className="koreader-sync">
                <button 
                    className="sync-btn"
                    onClick={syncWithKOReader}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <RefreshCw size={16} className="spinning" />
                    ) : (
                        <Download size={16} />
                    )}
                    Synchroniser avec KOReader
                </button>
                
                {syncStatus && (
                    <div className={`sync-result ${syncStatus.success ? 'success' : 'error'}`}>
                        {syncStatus.success ? (
                            <CheckCircle size={16} />
                        ) : (
                            <AlertCircle size={16} />
                        )}
                        <span>{syncStatus.message}</span>
                    </div>
                )}
            </div>

            {/* Messages d'erreur */}
            {error && (
                <div className="koreader-error">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {/* Informations */}
            <div className="koreader-info">
                <h4>À propos de KOReader</h4>
                <ul>
                    <li>Lecteur optimisé pour les écrans e-ink</li>
                    <li>Support de nombreux formats (PDF, EPUB, DjVu, etc.)</li>
                    <li>Interface sans animation, parfaite pour la lecture</li>
                    <li>Intégration native avec Calibre</li>
                </ul>
                
                <div className="koreader-links">
                    <a 
                        href="https://koreader.rocks/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="koreader-link"
                    >
                        <ExternalLink size={16} />
                        Site officiel
                    </a>
                    <a 
                        href="https://github.com/koreader/koreader" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="koreader-link"
                    >
                        <ExternalLink size={16} />
                        GitHub
                    </a>
                </div>
            </div>
        </div>
    );
};

export default KOReaderIntegration;
